package com.schoolconnect.app.repository.profile

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.AuthSessionStore
import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.datasource.network.request.AppStaffUpdateRequest
import com.schoolconnect.app.datasource.network.response.AppStaffResponse
import com.schoolconnect.app.datasource.network.response.ParentResponse
import com.schoolconnect.app.datasource.network.response.SchoolStaffResponse
import com.schoolconnect.app.datasource.network.response.resolveRoleCode
import com.schoolconnect.app.datasource.network.request.ParentUpdateRequest
import com.schoolconnect.app.datasource.network.request.SchoolStaffUpdateRequest
import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.util.scLog
import kotlinx.serialization.json.JsonObject

/** Self-service profile boundary. Profile IDs come from the server's /me records. */
object ProfileRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] ProfileRepository"

    suspend fun loadAppStaffProfile(): Result<AppStaffResponse> {
        val session = AuthSessionStore.getSession()?.response
        val appStaffId = session?.appStaffId?.trim()?.takeIf { it.isNotBlank() }
            ?: session?.identityContextProfiles
                ?.asSequence()
                ?.mapNotNull { it.appStaffId?.trim()?.takeIf(String::isNotBlank) }
                ?.firstOrNull()

        // Some backend deployments route /app-staff/me through the UUID path
        // parameter and reject "me". Prefer the concrete app_staff_id from
        // the authenticated session when it is available, while retaining the
        // documented self-service endpoint for sessions without that id.
        val request = appStaffId?.let { KtorApiClient.getAppStaffProfile(it) }
            ?: KtorApiClient.getCurrentAppStaffProfile()
        return request
            .onSuccess { scLog("$TAG appStaff profile loaded id=${it.id.orEmpty().ifBlank { "<blank>" }}") }
            .onFailure { scLog("$TAG appStaff profile failed", it) }
    }

    suspend fun loadUserProfile(userId: String): Result<JsonObject> {
        val normalizedUserId = userId.trim()
        if (normalizedUserId.isBlank()) {
            return Result.failure(IllegalArgumentException("userId is required"))
        }
        return KtorApiClient.getUserProfileRaw(normalizedUserId)
            .onSuccess { scLog("$TAG user profile loaded userId=$normalizedUserId") }
            .onFailure { scLog("$TAG user profile failed userId=$normalizedUserId", it) }
    }

    suspend fun loadSchoolStaffProfile(): Result<SchoolStaffResponse> =
        KtorApiClient.getCurrentSchoolStaffProfile()
            .onSuccess { scLog("$TAG schoolStaff profile loaded id=${it.id.orEmpty().ifBlank { "<blank>" }}") }
            .onFailure { scLog("$TAG schoolStaff profile failed", it) }

    suspend fun loadParentProfile(): Result<ParentResponse> =
        KtorApiClient.getCurrentParentProfile()
            .onSuccess { scLog("$TAG parent profile loaded id=${it.id.orEmpty().ifBlank { "<blank>" }}") }
            .onFailure { scLog("$TAG parent profile failed", it) }

    data class ProfileUpdate(
        val fullName: String,
        val pushNotificationsEnabled: Boolean,
        val emailNotificationsEnabled: Boolean,
        val photoFileId: String? = null
    )

    suspend fun uploadCurrentProfilePhoto(
        filename: String,
        contentType: String,
        bytes: ByteArray
    ): Result<String> {
        val userId = UserSessionStore.getUserId().trim().takeIf { it.isNotBlank() }
        return FileUploadRepository.uploadProfilePicture(userId, filename, contentType, bytes)
    }

    suspend fun updateCurrentProfile(roleCode: String, update: ProfileUpdate): Result<Unit> = runCatching {
        val normalizedRole = roleCode.trim()
        val userId = UserSessionStore.getUserId().trim()
        val photoFileId = update.photoFileId
        when (normalizedRole) {
            SchoolKeys.V_ROLE_PARENT -> {
                val profile = loadParentProfile().getOrElse { throw it }
                val id = profile.id?.trim().orEmpty().ifBlank { error("Parent profile id is unavailable") }
                KtorApiClient.updateParent(
                    id,
                    ParentUpdateRequest(
                        name = update.fullName.trim().ifBlank { null },
                        pushNotificationEnabled = update.pushNotificationsEnabled,
                        emailNotificationEnabled = update.emailNotificationsEnabled,
                        profileImageFileId = photoFileId
                    ),
                    currentUserId = userId
                ).getOrElse { throw it }
            }
            SchoolKeys.V_ROLE_SCHOOL_ADMIN,
            SchoolKeys.V_ROLE_TEACHER,
            SchoolKeys.V_ROLE_SUPPORT_STAFF -> {
                val profile = loadSchoolStaffProfile().getOrElse { throw it }
                val request = SchoolStaffUpdateRequest(
                    name = update.fullName.trim().ifBlank { null },
                    pushNotificationEnabled = update.pushNotificationsEnabled,
                    emailNotificationEnabled = update.emailNotificationsEnabled,
                    profileImageFileId = photoFileId
                )
                if (normalizedRole == SchoolKeys.V_ROLE_TEACHER) {
                    KtorApiClient.updateTeacherOwnProfile(request).getOrElse { throw it }
                } else {
                    val id = profile.id?.trim().orEmpty().ifBlank { error("Staff profile id is unavailable") }
                    KtorApiClient.updateSchoolStaff(id, request, userId).getOrElse { throw it }
                }
            }
            else -> {
                // verify-otp returns the role entity ID separately from the
                // authorization assignment ID. For platform roles, use the
                // persisted app_staff_id directly; role_assignment_id is not
                // a valid path parameter for /app-staff/{staff_id}.
                val storedAppStaffId = AuthSessionStore.getSession()
                    ?.response
                    ?.identityContextProfiles
                    .orEmpty()
                    .firstOrNull { it.resolveRoleCode() == normalizedRole }
                    ?.appStaffId
                    ?.trim()
                    .orEmpty()
                val id = storedAppStaffId.ifBlank {
                    loadAppStaffProfile().getOrElse { throw it }.id?.trim().orEmpty()
                }.ifBlank {
                    error("App staff profile id is unavailable for this role")
                }
                KtorApiClient.updateAppStaff(
                    id,
                    AppStaffUpdateRequest(
                        name = update.fullName.trim().ifBlank { null },
                        pushNotificationEnabled = update.pushNotificationsEnabled,
                        emailNotificationEnabled = update.emailNotificationsEnabled,
                        profileImageFileId = photoFileId
                    ),
                    currentUserId = userId
                ).getOrElse { throw it }
            }
        }
        scLog("$TAG updateCurrentProfile success role=$normalizedRole photo=${photoFileId != null}")
    }.onFailure { scLog("$TAG updateCurrentProfile failed role=${roleCode.trim()}", it) }
}
